import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ClientTierConfiguration } from 'src/app/admin/dashboards/client-list/dialogs/client-tier-configuration/models/client-tier-configurarion.model';
import { environment } from 'src/environments/environment';
import { FirestoreService } from '../firestore.service';
import { AWVSetup, Client } from 'src/app/models/client.model';

/**
 * Per-clinic switch for the care call guide's tiered escalations (BRD-CLIN-2026-006 §4.1).
 *
 * Deliberately NOT `isChfThresholdsEnabled`. That one gates the RPM engine's automatic weight
 * alerts (`vital_flag_clients`); reusing it would mean turning the guide's escalations on and off
 * with the device-reading thresholds, which is the double-alerting CHF-E2 exists to prevent.
 */
export const CHF_CARE_CALL_ALERTS_TOGGLE = 'isChfCareCallAlertsEnabled';

/**
 * Per-clinic switch for CCM care plan time (BRD-CLIN-2026-008 §4.2): the care plan picker on CCM time
 * and the server rule behind it. On for GLB only at launch; every other clinic keeps today's time form
 * until it is turned on for them.
 */
export const CCM_CARE_PLAN_TIME_TOGGLE = 'isCcmCarePlanTimeEnabled';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  constructor(private http: HttpClient, private firestoreService: FirestoreService) {}

  addClientRule(clientRule: any) {
    const url = `${environment.welbyEndpoint}/api/v1/core/clients/${clientRule.clientId}/rules`;
    return this.http.post(url, clientRule);
  }

  getClientRules(clientId: string) {
    const url = `${environment.welbyEndpoint}/api/v1/core/clients/${clientId}/rules`;
    return this.http.get(url);
  }

  /**
   * Whether this clinic has been onboarded to the Emory CHF escalation SOC.
   *
   * FAILS CLOSED. A missing client doc, a missing toggle, an explicit `false` and a read error all
   * resolve to `false`, so a clinic that was never onboarded cannot have care call findings routed
   * into its NCM queue. The guide document itself is global — this is the only thing standing
   * between the §4.1 bands and all 40 clinics.
   *
   * `take(1)` matters: `doc$` is a live stream, and the caller composes this into a `forkJoin`
   * that would never emit against an Observable that does not complete.
   */
  isCareCallAlertsEnabled$(clientId: string): Observable<boolean> {
    return this.isClinicToggleOn$(clientId, CHF_CARE_CALL_ALERTS_TOGGLE);
  }

  /**
   * Whether this clinic has CCM care plan time switched on. Pass the patient's
   * `client_responsible_id`, which is the `clients` document id.
   *
   * FAILS CLOSED, like the care call alerts above: a clinic that is off keeps today's time form with
   * no picker, while a wrong `true` would block CCM saves at a clinic
   * that never agreed to the requirement. The server will read the same switch on its own (S2.2)
   * and its answer is the one that counts: when it rejects a save for missing care plan items, the
   * time form keeps the entry and shows why rather than assume this value matches it.
   */
  isCcmCarePlanTimeEnabled$(clientId: string): Observable<boolean> {
    return this.isClinicToggleOn$(clientId, CCM_CARE_PLAN_TIME_TOGGLE);
  }

  getDefaultClientRulesTypes() {
    return this.firestoreService.doc$('welby_configuration/client_rules_type');
  }

  getTierConfiguration(clientId: string) {
    return this.firestoreService.doc$(`vital_flag_assignments/${clientId}`);
  }

  getDefaultTierConfiguration() {
    return this.firestoreService.doc$(`welby_configuration/vital_flag_assignments`);
  }

  async saveTierConfiguration(clientId: string, configuration: ClientTierConfiguration) {
    return this.firestoreService.set(`vital_flag_assignments/${clientId}`, configuration);
  }

  async saveAWVEmailTemplate(clientId: string, emailTemplate: AWVSetup) {
    return this.firestoreService.update(`clients/${clientId}`, { awv_setup: emailTemplate });
  }

  /**
   * Reads one per-clinic toggle off `clients/{id}.featureToggles`. Only the boolean `true` counts:
   * a missing doc, a missing key, a hand-typed string "true" and a read error are all `false`.
   *
   * `take(1)` matters: `doc$` is a live stream, and the care call guide composes this into a
   * `forkJoin` that would never emit against an Observable that does not complete.
   */
  private isClinicToggleOn$(
    clientId: string,
    toggleKey: typeof CHF_CARE_CALL_ALERTS_TOGGLE | typeof CCM_CARE_PLAN_TIME_TOGGLE,
  ): Observable<boolean> {
    if (!clientId) {
      return of(false);
    }
    return this.firestoreService.doc$<Client>(`clients/${clientId}`).pipe(
      take(1),
      map((client) => client?.featureToggles?.[toggleKey] === true),
      catchError(() => of(false)),
    );
  }
}
